<script setup lang="ts">
import { ref } from 'vue'
import { useDark, useToggle } from '@vueuse/core'

import { DropdownActionsData } from '@types'

import { useUser } from '../utils/useUser'

import SubscriptionPlan from './SubscriptionPlan.vue'
import User from './User.vue'
import {
  DSDropdownMenu, 
  DSDropdownMenuContent, 
  DSDropdownMenuItem, 
  DSDropdownMenuTrigger, 
  DSSwitch
} from '@ds'
import { MoonIcon } from '@lucide/vue'

const appVersion = `${__APP_VERSION__ || ''}`

const open = ref(false)

const isDark = useDark()
const toggleDark = useToggle(isDark)

const { menuItems } = useUser()

const closeMenu = () => {
  open.value = false
}

const handleSelect = (item: DropdownActionsData) => {
  item.action?.(closeMenu)
}
</script>

<template>
  <DSDropdownMenu v-model:open="open">
    <DSDropdownMenuTrigger as-child>
      <User data-refid="userDropdown" />
    </DSDropdownMenuTrigger>
    <DSDropdownMenuContent
      align="end"
      class="w-68 rounded-ds-lg p-0 overflow-hidden"
    >
      <div class="p-1.5 border-b border-border">
        <DSDropdownMenuItem
          as-child
          class="h-auto py-2 w-full"
        >
          <SubscriptionPlan />
        </DSDropdownMenuItem>
      </div>
      <div class="p-1.5">
        <label class="flex items-center gap-2 px-2 py-2.25 text-sm rounded-ds-sm hover:bg-accent hover:text-accent-foreground cursor-pointer">
          <component
            :is="MoonIcon"
            class="size-4"
            aria-hidden="true"
          />
          Dark theme
          <DSSwitch
            :model-value="isDark"
            class="ml-auto"
            @update:model-value="toggleDark"
          />
        </label>
        <DSDropdownMenuItem
          v-for="item in menuItems"
          :key="item.value ?? item.label"
          class="h-9.5"
          :disabled="item.disabled"
          :data-refid="`userDropdown${(item.value ?? item.label).replace(' ', '')}`"
          @select="handleSelect(item)"
        >
          <component
            :is="item.icon"
            v-if="item.icon"
            class="size-4"
            aria-hidden="true"
          />
          <span class="flex-1 truncate">{{ item.label }}</span>
        </DSDropdownMenuItem>
      </div>
      <div
        v-if="appVersion"
        class="px-4 py-2.5 border-t border-border text-xs font-medium text-muted-foreground/50"
      >
        Version {{ appVersion }}
      </div>
    </DSDropdownMenuContent>
  </DSDropdownMenu>
</template>
